import BGFImg1 from '@/assets/img/corporate-and-organization/bgf_img01@2x.jpg';
import BGFImg2 from '@/assets/img/corporate-and-organization/bgf_img02@2x.jpg';
import BGFLogo from '@/assets/img/corporate-and-organization/bgf_logo@2x.png';
import BGFLogoM from '@/assets/img/corporate-and-organization/bgf_logo_m@2x.png';
import Button from '@/components/Button';
import Container from '@/components/Container';
import Image from '@/components/Image';
import LinkSafe from '@/components/LinkSafe';
import { Tit } from '@/components/Titles';
import { breakpoint } from '@/helpers/BreakpointHelper';
import LayoutWithTitle from '@/layouts/LayoutWithTitle';
import { navigate, PageProps } from 'gatsby';
import React from 'react';
import styled from 'styled-components';

const Flex = styled.div`
  display: flex;
`;
const ImgBox = styled.div`
  position: relative;
  width: 100%;

  .img_copyright {
    font-size: 12px;
    color: #fff;
    opacity: 0.5;
    position: absolute;
    bottom: 23px;
    left: 20px;

    ${breakpoint(`mobile`)} {
      bottom: 13px;
      left: 10px;
    }
  }
`;
const SectionHead = styled.section`
  background: #f8fcfe;
  padding: 96px 0 120px;

  ${breakpoint(`mobile`)} {
    padding: 72px 0 44px;

    .br_pc {
      display: none;
    }
  }

  ${Flex} {
    align-items: flex-end;
    gap: 60px;

    ${breakpoint(`tablet`)} {
      flex-direction: column;
    }
    ${breakpoint(`mobile`)} {
      gap: 32px;
    }
  }

  ${ImgBox} {
    width: 57%;
    ${breakpoint(`tablet`)} {
      width: 100%;
    }
  }

  .company_info {
    ${breakpoint(`tablet`)} {
      width: 100%;
    }
    .company_logo img {
      width: auto;
    }

    .company_name {
      font-size: 40px;
      font-weight: 700;
      margin: 30px 0 40px;
    }

    ul {
      border-top: 1px solid #2d2926;
      padding-top: 20px;

      li {
        display: flex;
        align-items: center;
        position: relative;

        & + li {
          margin-top: 6px;
        }

        &::before {
          content: '';
          display: block;
          width: 4px;
          height: 4px;
          border-radius: 100%;
          background: #1cabe2;
          position: absolute;
          top: 11px;
          left: 0;
          flex-shrink: 0;
        }

        .info_tit {
          font-weight: 700;
          color: #1cabe2;
          width: 102px;
          flex-shrink: 0;
          margin-right: 45px;
          padding-left: 12px;
        }
      }
    }
    ${breakpoint(`mobile`)} {
      .company_name {
        font-size: 24px;
        margin: 12px 0 20px;
      }

      ul {
        li {
          line-height: 28px;
          & + li {
            margin-top: 4px;
          }

          .info_tit {
            width: 92px;
            margin-right: 30px;
          }
        }
      }
    }
  }
`;
const SectionBody = styled.section`
  padding-top: 120px;
  .br_m {
    display: none;
  }
  p {
    margin-top: 32px;
    line-height: 32px;
  }

  ${breakpoint(`mobile`)} {
    padding-top: 60px;

    .br_pc {
      display: none;
    }
    .br_m {
      display: block;
    }
    p {
      font-size: 14px;
      line-height: 28px;
    }
  }

  ${Container} {
    & + ${Container} {
      margin-top: 96px;

      ${breakpoint(`mobile`)} {
        margin-top: 72px;
      }
    }
  }

  .video_wrap {
    width: 100%;
    padding-top: 56.25%;
    position: relative;
    margin-top: 32px;

    .video {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
    }
  }

  ${ImgBox} {
    margin-top: 32px;
    .img_copyright {
      opacity: 1;
    }
  }

  .btn_wrap {
    margin-top: 64px;
    text-align: center;

    ${breakpoint(`mobile`)} {
      margin-top: 48px;
    }
    ${Button} {
      font-size: 16px;
    }
  }
`;

const BgfRetail: React.FC<PageProps> = ({ location }) => (
  <LayoutWithTitle
    location={location}
    title="기업·기관 사회공헌"
    description="for every child, partnerships"
  >
    <SectionHead>
      <Container>
        <Flex>
          <ImgBox>
            <img src={BGFImg1} alt="© UNICEF/UNI719423/Sokhin 이미지" />
            <div className="img_copyright">© UNICEF/UNI719423/Sokhin</div>
          </ImgBox>
          <div className="company_info">
            <div className="company_logo">
              <Image
                pcSrc={BGFLogo}
                mobileSrc={BGFLogoM}
                alt="Kakaobank 로고"
              />
            </div>
            <div className="company_name">BGF리테일</div>
            <ul>
              <li>
                <div className="info_tit">산업군</div>
                <div className="info_con">유통</div>
              </li>
              <li>
                <div className="info_tit">파트너십 레벨</div>
                <div className="info_con">National</div>
              </li>
              <li>
                <div className="info_tit">홈페이지</div>
                <div className="info_con">
                  <LinkSafe to="https://www.bgfretail.com/" target="_blank">
                    https://www.bgfretail.com/
                  </LinkSafe>
                </div>
              </li>
              <li>
                <div className="info_tit">지원 분야​</div>
                <div className="info_con">Climate Change</div>
              </li>
            </ul>
          </div>
        </Flex>
      </Container>
    </SectionHead>
    <SectionBody>
      <Container>
        <Tit size="s1-5">BGF리테일은</Tit>
        <p>
          BGF리테일과 유니세프 한국위원회는 2011년부터 전국 매장에서{' '}
          <strong>CU 사랑의 동전 모으기 캠페인</strong>을 통해 조성된 기금으로{' '}
          <br className="br_pc" />
          개발 도상국 어린이 교육 및 건강을 위한 구호 활동에 지원하고 있습니다.
          BGF리테일은 동전 기부금과 BGF리테일의 기부금을 더해{' '}
          <br className="br_pc" />
          2021년부터{' '}
          <strong>
            몽골 탄소 저감 및 대기 질 개선을 위한 몽골 친환경 고효율 게르 지원
            사업에 지원
          </strong>
          하고 있습니다. <br />
          몽골은 가뭄, 혹한 등과 같은 기후 위기에 더해 게르에서 태우는 대량의
          쓰레기로 대기오염이 매우 심각한 나라입니다. <br />
          예를 들어, 울란바트로의 초미세먼지(PM2.5 입자의) 연간 평균 농도는
          2022년 WHO 권장 수준보다 9배 더 높았습니다. <br />
          몽골의 어린이들은 겨울철 외에도 폐렴, 기관지염, 천식 등 다양한 호흡기
          관련 질환을 겪으며 건강을 위협받고 있습니다.
        </p>
        <div className="video_wrap">
          <iframe
            className="video"
            src="https://www.youtube.com/embed/mcMNmfvSAoM"
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </Container>
      <Container>
        <Tit size="s1-5">BGF리테일의 지원으로</Tit>
        <p>
          <strong>
            유니세프는 몽골 지방정부, 몽골과학기술대학교 및 국제학술기구 등과
            협업해 전기를 이용한 조리, 난방, 단열 제품(Cooking, Heating, and
            Insulation Products, CHIP) 패키지를 개발했습니다. CHIP은 몽골의
            전통을 지키며 공기질과 아동 노동 문제 해결에 일조하는 지속가능한
            새로운 주거 양식입니다.
          </strong>{' '}
          <br />
          CHIP을 통해 몽골의 어린이들은 집 안에서 오염된 공기에 노출되지 않게
          되었습니다. 또한, 불 피우기 노동에 동원되지 않아도 되어 더 많은 시간을
          놀이와 배움에 할애할 수 있게 됐습니다.
        </p>
        <p>
          BGF리테일과 유니세프 한국위원회는 이를 울란바토르 내 가구와 유치원에
          공급함으로써, 몽골의 공기질 개선, 가구 내 어린이 안전 확보 및 유치원
          출석률 개선에 기여하고 있습니다. <br />
          또한 현지의 녹색 일자리 창출에도 기여했습니다. <br className="br_m" />
          2021년부터 2023년까지 CHIP을 통해 31%의 실내 공기질 개선 효과를
          이뤘으며, <br className="br_pc" />
          현재까지 BGF리테일과 유니세프 한국위원회는{' '}
          <strong>
            483가구, 16개 유치원, 2,722명의 어린이와 청소년에게 혜택을
            제공했습니다.
          </strong>
        </p>
        <ImgBox>
          <img src={BGFImg2} alt="© UNICEF/UN0220810/Matas 이미지" />
          <div className="img_copyright">© UNICEF/UN0220810/Matas</div>
        </ImgBox>
      </Container>
      <div className="btn_wrap">
        <Button
          className="btn"
          color="grey"
          width={256}
          onClick={() =>
            navigate(`/involve/corporate-and-organization/social-contribution`)
          }
        >
          뒤로가기
        </Button>
      </div>
    </SectionBody>
  </LayoutWithTitle>
);

export default BgfRetail;
